import { trackEvent } from './analytics';
import { storedFirstTouch } from './attribution';
import { appStorage, runtimeConfig } from './runtime';

/**
 * GR-002 - canonical funnel event contract.
 *
 * This file is the canonical definition of the funnel. The landing and auth
 * surfaces repeat only the wire names they emit because neither imports this
 * bundle; their exceptions are recorded in GR-002.
 *
 * GR-001 rule still holds: pages and components never call the tracker directly.
 * They call `emitFunnelEvent` / `trackFunnelSignupState` from here, and this
 * module is the only caller of `trackEvent`.
 *
 * Privacy: no personal data ever reaches an event. The properties attached are the
 * surface (web / android / ios), the first-touch campaign origin and the visitor
 * language - campaign parameters and a language tag, never personal data - and
 * `sanitizeEventData` in analytics.ts is a second line of defence. Funnel bookkeeping
 * in the app holds booleans and timestamps and contains no identifier. The auth
 * surface uses session-only delivery markers; the anonymous landing writes nothing.
 */

export type FunnelEventScope = 'once-per-device' | 'once-per-session' | 'always';

/**
 * GR-004 - dimensions every canonical event carries.
 *
 * The weekly acquisition panel has to read every step by origin and by language,
 * including `activated`, which happens days later in a different analytics session.
 * The provider only keeps `utm_*` on the event whose URL carried them, and the OIDC
 * redirect destroys that query string, so the dimensions travel with the event
 * instead: assigned here, in the one module that talks to the tracker, and never at
 * the call sites.
 */
export const FUNNEL_LANGUAGES = ['en', 'pt', 'es', 'fr'] as const;

export const OTHER_LANGUAGE = 'other';

export const DIRECT_SOURCE = 'direct';

export const NO_MEDIUM = 'none';

export const AUTH_PROVIDER_CLAIM = 'senti_auth_provider';

export const SIGNUP_PROVIDERS = ['password', 'google'] as const;

export type SignupProvider = typeof SIGNUP_PROVIDERS[number];

export const resolveSignupProvider = (value: unknown): SignupProvider => {
  const candidate = Array.isArray(value) ? value[0] : value;

  return candidate === 'google' ? 'google' : 'password';
};

const SOURCE_MAX_LENGTH = 64;
const MEDIUM_MAX_LENGTH = 64;
const CAMPAIGN_MAX_LENGTH = 128;

/**
 * Mirrors `UtmConvention.Normalize` on the server: lowercase, keep [a-z0-9._], turn
 * every other run of characters into a single separator, trim and cap. The stored
 * record keeps the raw value because the server is the authority on bucketing; what
 * reaches the dashboard is bucketed here, so the panel and the server table speak
 * the same vocabulary instead of two spellings of the same channel.
 */
export const normalizeDimension = (value: string | undefined | null, maxLength: number): string | undefined => {
  if (!value) {
    return undefined;
  }

  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._]+/g, '-')
    .slice(0, maxLength)
    .replace(/^[-._]+|[-._]+$/g, '');

  return normalized.length > 0 ? normalized : undefined;
};

/** Reduces a browser language tag to the four supported languages, or `other`. */
export const resolveLanguage = (tag?: string | null): string => {
  const base = (tag ?? '').trim().toLowerCase().split(/[-_]/)[0];

  return (FUNNEL_LANGUAGES as readonly string[]).includes(base) ? base : OTHER_LANGUAGE;
};

const currentLanguage = (): string =>
  resolveLanguage(typeof navigator !== 'undefined' ? navigator.language : undefined);

/**
 * The non-personal dimensions of one event. Never throws: a device without a stored
 * origin still reports `direct` / `none`, so a missing record shows up as unattributed
 * traffic and never as a hole in the funnel.
 */
export const funnelDimensions = (): Record<string, string> => {
  const dimensions: Record<string, string> = {
    surface: runtimeConfig.platform,
    source: DIRECT_SOURCE,
    medium: NO_MEDIUM,
    lang: currentLanguage(),
  };

  try {
    const origin = storedFirstTouch();

    if (origin) {
      dimensions.source = normalizeDimension(origin.source, SOURCE_MAX_LENGTH) ?? DIRECT_SOURCE;
      dimensions.medium = normalizeDimension(origin.medium, MEDIUM_MAX_LENGTH) ?? NO_MEDIUM;

      const campaign = normalizeDimension(origin.campaign, CAMPAIGN_MAX_LENGTH);

      if (campaign) {
        dimensions.campaign = campaign;
      }
    }
  } catch {
    // Reporting `direct` beats not reporting the step at all.
  }

  return dimensions;
};

/**
 * Anonymous dimensions carried on the OIDC authorization request. The auth
 * server emits `signup_start` only after the person has chosen a provider, so it
 * needs the same first-touch values that later funnel steps receive in the app.
 */
export const authFunnelQueryParameters = (): Record<string, string> => {
  const dimensions = funnelDimensions();
  const parameters: Record<string, string> = {
    funnel_surface: dimensions.surface,
    funnel_source: dimensions.source,
    funnel_medium: dimensions.medium,
    funnel_lang: dimensions.lang,
  };

  if (dimensions.campaign) {
    parameters.funnel_campaign = dimensions.campaign;
  }

  return parameters;
};

type FunnelEventDefinition = {
  /** Wire name. Stable contract - never rename without a migration note. */
  readonly name: string;
  readonly scope: FunnelEventScope;
  /** Emitted only after this step is already recorded on this device. */
  readonly requires?: string;
  /** Never emitted again once this step is recorded on this device. */
  readonly notAfter?: string;
  /** Where the event is emitted from. Documentation only. */
  readonly emittedBy: string;
};

const FUNNEL_EVENT_DEFINITIONS = {
  /** Arrival on the public surface. */
  landingView: {
    name: 'landing_view',
    scope: 'always',
    emittedBy: 'landing/analytics.js',
  },
  /** Registration CTA click / OIDC redirect. */
  signupStart: {
    name: 'signup_start',
    scope: 'once-per-session',
    notAfter: 'signupCompleted',
    emittedBy: 'Astro.OpeniddictServer signup-funnel.js',
  },
  /** Account created. Detected from the account creation instant. */
  signupCompleted: {
    name: 'signup_completed',
    scope: 'once-per-device',
    emittedBy: 'src/ProfileGuard.tsx',
  },
  /** External registration left while its required profile was incomplete. */
  profileCompletionAbandoned: {
    name: 'profile_completion_abandoned',
    scope: 'once-per-session',
    emittedBy: 'Astro.OpeniddictServer signup-funnel.js',
  },
  /** Birth date, time and place stored. */
  birthRecordCompleted: {
    name: 'birth_record_completed',
    scope: 'once-per-device',
    requires: 'signupCompleted',
    emittedBy: 'src/ProfileGuard.tsx',
  },
  /** First natal chart rendered. */
  chartViewed: {
    name: 'chart_viewed',
    scope: 'once-per-device',
    requires: 'signupCompleted',
    emittedBy: 'src/components/MapaAstral.tsx',
  },
  /** Second distinct session within 7 days of the first one. */
  activated: {
    name: 'activated',
    scope: 'once-per-device',
    requires: 'signupCompleted',
    emittedBy: 'src/ProfileGuard.tsx',
  },
  /**
   * Checkout steps. Names are reserved here so the funnel has a single
   * definition; emission belongs to the subscriptions initiative.
   */
  checkoutStart: {
    name: 'checkout_start',
    scope: 'always',
    emittedBy: 'subscriptions initiative (not emitted by GR-002)',
  },
  paid: {
    name: 'paid',
    scope: 'always',
    emittedBy: 'subscriptions initiative (not emitted by GR-002)',
  },
} as const;

export type FunnelEventKey = keyof typeof FUNNEL_EVENT_DEFINITIONS;

export const funnelEventName = (key: FunnelEventKey): string =>
  FUNNEL_EVENT_DEFINITIONS[key].name;

const definitionOf = (key: FunnelEventKey): FunnelEventDefinition =>
  FUNNEL_EVENT_DEFINITIONS[key] as FunnelEventDefinition;

export const FUNNEL_STORAGE_KEY = 'senti.funnel.v1';
export const FUNNEL_SESSION_KEY = 'senti.funnel.session';

/** A second session counts as activation only inside this window. */
export const ACTIVATION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * How recently the account must have been created for the first authenticated
 * load to count as `signup_completed`. Registration redirects straight back into
 * the app, so minutes are enough; the window only guards against a device that
 * never loaded the app right after registering.
 */
export const SIGNUP_RECENCY_MS = 60 * 60 * 1000;

type FunnelState = {
  emitted: string[];
  firstSessionAt?: number;
  sessions: number;
};

const emptyState = (): FunnelState => ({ emitted: [], sessions: 0 });

const readState = (): FunnelState => {
  try {
    const raw = appStorage.local.getItem(FUNNEL_STORAGE_KEY);

    if (!raw) {
      return emptyState();
    }

    const parsed = (JSON.parse(raw) ?? {}) as Partial<FunnelState>;
    const emitted = Array.isArray(parsed.emitted)
      ? parsed.emitted.filter((entry): entry is string => typeof entry === 'string')
      : [];

    return {
      emitted,
      firstSessionAt:
        typeof parsed.firstSessionAt === 'number' ? parsed.firstSessionAt : undefined,
      sessions: typeof parsed.sessions === 'number' ? parsed.sessions : 0,
    };
  } catch {
    return emptyState();
  }
};

const writeState = (state: FunnelState): void => {
  try {
    appStorage.local.setItem(FUNNEL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage can be unavailable (private mode, quota). The funnel degrades to
    // best effort; it must never break the app.
  }
};

const sessionMarkerKey = (key: FunnelEventKey): string =>
  `${FUNNEL_SESSION_KEY}.${funnelEventName(key)}`;

const wasEmittedThisSession = (key: FunnelEventKey): boolean => {
  try {
    return appStorage.session.getItem(sessionMarkerKey(key)) === '1';
  } catch {
    return false;
  }
};

const markEmittedThisSession = (key: FunnelEventKey): void => {
  try {
    appStorage.session.setItem(sessionMarkerKey(key), '1');
  } catch {
    // Best effort, as above.
  }
};

export const hasEmittedFunnelEvent = (key: FunnelEventKey): boolean =>
  readState().emitted.includes(funnelEventName(key));

const isAllowed = (key: FunnelEventKey, state: FunnelState): boolean => {
  const definition = definitionOf(key);

  if (definition.requires && !state.emitted.includes(funnelEventName(definition.requires as FunnelEventKey))) {
    return false;
  }

  if (definition.notAfter && state.emitted.includes(funnelEventName(definition.notAfter as FunnelEventKey))) {
    return false;
  }

  if (definition.scope === 'once-per-device' && state.emitted.includes(definition.name)) {
    return false;
  }

  if (definition.scope === 'once-per-session' && wasEmittedThisSession(key)) {
    return false;
  }

  return true;
};

/**
 * Emits one canonical funnel event, applying the scope and ordering rules of its
 * definition. Returns whether the tracker accepted the event for delivery.
 */
export const emitFunnelEvent = (
  key: FunnelEventKey,
  data?: Record<string, string | number | boolean>
): boolean => {
  try {
    const definition = definitionOf(key);
    const state = readState();

    if (!isAllowed(key, state)) {
      return false;
    }

    if (!trackEvent(definition.name, { ...funnelDimensions(), ...data })) {
      return false;
    }

    if (definition.scope === 'once-per-session') {
      markEmittedThisSession(key);
    }

    if (definition.scope === 'once-per-device' && !state.emitted.includes(definition.name)) {
      writeState({ ...state, emitted: [...state.emitted, definition.name] });
    }

    return true;
  } catch {
    // Measuring the funnel must never be able to break a screen. Callers sit on
    // the authenticated path, where a throw would look like a failed session.
    return false;
  }
};

/**
 * Counts one distinct app session. Called once per app start-up, before any
 * funnel event is emitted, so that `activated` can be decided later.
 */
export const beginFunnelSession = (now: number = Date.now()): void => {
  try {
    if (appStorage.session.getItem(FUNNEL_SESSION_KEY) === '1') {
      return;
    }

    appStorage.session.setItem(FUNNEL_SESSION_KEY, '1');
  } catch {
    // Without session storage every load would count as a new session, which
    // would inflate activation. Counting nothing is the safer failure.
    return;
  }

  const state = readState();

  writeState({
    ...state,
    firstSessionAt: state.firstSessionAt ?? now,
    sessions: state.sessions + 1,
  });
};

const hasReachedActivation = (now: number): boolean => {
  const { firstSessionAt, sessions } = readState();

  if (typeof firstSessionAt !== 'number' || sessions < 2) {
    return false;
  }

  return now - firstSessionAt <= ACTIVATION_WINDOW_MS;
};

/** Non-personal projection of the account, built by the caller. */
export type FunnelAccountSnapshot = {
  /** When the account was created, as reported by the API. */
  readonly createdAtUtc?: Date | string | null;
  /** Whether birth date, time and place are all stored. */
  readonly hasBirthRecord: boolean;
  /** Method that established the auth session, from a bounded OIDC claim. */
  readonly provider: SignupProvider;
};

const asTimestamp = (value?: Date | string | null): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = value instanceof Date ? value : new Date(value);
  const time = parsed.getTime();

  return Number.isNaN(time) ? undefined : time;
};

/**
 * Single entry point for the authenticated part of the funnel. Called from the
 * one place every signed-in route passes through, with a projection that carries
 * no personal data.
 */
export const trackAuthenticatedFunnelState = (
  account: FunnelAccountSnapshot,
  now: number = Date.now()
): void => {
  const createdAt = asTimestamp(account.createdAtUtc);

  if (typeof createdAt === 'number' && now - createdAt <= SIGNUP_RECENCY_MS) {
    emitFunnelEvent('signupCompleted', { provider: account.provider });
  }

  if (account.hasBirthRecord) {
    emitFunnelEvent('birthRecordCompleted');
  }

  if (hasReachedActivation(now)) {
    emitFunnelEvent('activated');
  }
};

/** Test helper. Clears every funnel marker on this device. */
export const resetFunnelState = (): void => {
  try {
    appStorage.local.removeItem(FUNNEL_STORAGE_KEY);
    appStorage.session.removeItem(FUNNEL_SESSION_KEY);
    Object.keys(FUNNEL_EVENT_DEFINITIONS).forEach(key => {
      appStorage.session.removeItem(sessionMarkerKey(key as FunnelEventKey));
    });
  } catch {
    // Nothing to clear.
  }
};
